/**
 * MA-PPB-FUNNEL-060: Shared Consent Editor
 *
 * Clean abstraction over HTML consent text.
 * - Renders stored HTML as readable text with clickable links (never raw markup)
 * - Provides link insertion for Privacy Policy and Terms of Use
 * - Used in BOTH PPB pre-gen config and Funnel Builder consent step editor
 *
 * Storage: HTML string (consent_text field)
 * Display: contentEditable div that renders HTML natively
 *
 * Link insertion behavior:
 *   - Text selected: wraps selection with <a> tag
 *   - No selection: inserts link text (e.g. "Privacy Policy") at cursor
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * ConsentEditor — rich text consent editor
 *
 * Props:
 *   value             — HTML string (consent_text)
 *   onChange           — (html: string) => void
 *   privacyUrl         — current privacy policy URL
 *   termsUrl           — current terms of use URL
 *   onPrivacyUrlChange — (url: string) => void
 *   onTermsUrlChange   — (url: string) => void
 *   placeholder        — optional placeholder text
 *   className          — optional container className
 */
export function ConsentEditor({
  value,
  onChange,
  privacyUrl = "",
  termsUrl = "",
  onPrivacyUrlChange,
  onTermsUrlChange,
  placeholder = "Enter your consent language...",
  className,
}) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const lastValueRef = useRef(value);

  // Local state for URL inputs to avoid keystroke-level re-renders
  const [localPrivacyUrl, setLocalPrivacyUrl] = useState(privacyUrl);
  const [localTermsUrl, setLocalTermsUrl] = useState(termsUrl);

  // Sync local URL state when props change externally
  useEffect(() => { setLocalPrivacyUrl(privacyUrl); }, [privacyUrl]);
  useEffect(() => { setLocalTermsUrl(termsUrl); }, [termsUrl]);

  // Initialize editor content from value (only when value changes externally)
  useEffect(() => {
    if (!editorRef.current) return;
    // Only update DOM if value changed externally (not from our own edits)
    if (value !== lastValueRef.current && !isFocused) {
      editorRef.current.innerHTML = value || "";
      lastValueRef.current = value;
    }
  }, [value, isFocused]);

  // Set initial content on mount
  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle content changes from contentEditable
  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastValueRef.current = html;
    onChange(html);
  }, [onChange]);

  // Insert a link wrapping the current selection, or insert link text at cursor
  const insertLink = useCallback((url, linkText) => {
    if (!url || !editorRef.current) return;
    editorRef.current.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;

    const selectedText = selection.toString().trim();

    if (selectedText) {
      // Wrap selected text with link
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.textContent = selectedText;
      range.deleteContents();
      range.insertNode(anchor);
    } else {
      // No selection — insert link text at cursor position
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.textContent = linkText;
      range.deleteContents();
      range.insertNode(anchor);
      // Move cursor after the inserted link
      range.setStartAfter(anchor);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    emitChange();
  }, [emitChange]);

  const handleInsertPrivacy = useCallback(() => {
    insertLink(localPrivacyUrl, "Privacy Policy");
  }, [insertLink, localPrivacyUrl]);

  const handleInsertTerms = useCallback(() => {
    insertLink(localTermsUrl, "Terms of Use");
  }, [insertLink, localTermsUrl]);

  // Remove link from selection
  const handleRemoveLink = useCallback(() => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("unlink");
    emitChange();
  }, [emitChange]);

  // Paste as plain text only
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }, []);

  const isEmpty = !value || value === "<br>" || value.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div className={cn("space-y-3", className)}>
      {/* Rich text editor */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-300">Consent Language</Label>
        <div
          className={cn(
            "relative rounded-md border transition-colors",
            isFocused
              ? "border-indigo-500/40 ring-1 ring-indigo-500/20"
              : "border-slate-700/40 hover:border-slate-600/50"
          )}
        >
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className={cn(
              "min-h-[120px] max-h-[240px] overflow-y-auto px-3 py-2.5 text-sm leading-relaxed",
              "bg-slate-800/40 text-slate-100 rounded-md outline-none",
              "[&_a]:text-indigo-400 [&_a]:underline [&_a]:cursor-pointer"
            )}
            onInput={emitChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              emitChange();
            }}
            onPaste={handlePaste}
            data-testid="consent-editor-rich"
          />
          {/* Placeholder overlay */}
          {isEmpty && !isFocused && (
            <div
              className="absolute inset-0 px-3 py-2.5 text-sm text-slate-500 pointer-events-none"
            >
              {placeholder}
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-300">
          Links appear as clickable text. Select text then click a link button to apply.
        </p>
      </div>

      {/* Link URL inputs + insert buttons */}
      <div className="space-y-2">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-[10px] text-slate-300 uppercase tracking-wider">Privacy Policy URL</Label>
            <Input
              value={localPrivacyUrl}
              onChange={(e) => setLocalPrivacyUrl(e.target.value)}
              onBlur={() => onPrivacyUrlChange?.(localPrivacyUrl)}
              placeholder="https://example.com/privacy"
              className="h-7 text-xs bg-slate-800/40 border-slate-700/40 text-slate-200 placeholder:text-slate-600"
              data-testid="consent-privacy-url"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleInsertPrivacy}
            disabled={!localPrivacyUrl}
            className="h-7 px-2 text-[10px] border-slate-700/40 text-slate-300 hover:text-indigo-300 hover:border-indigo-500/30 disabled:opacity-40"
            data-testid="consent-insert-privacy-btn"
          >
            <Link2 className="h-3 w-3 mr-1" />
            Insert
          </Button>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-[10px] text-slate-300 uppercase tracking-wider">Terms of Use URL</Label>
            <Input
              value={localTermsUrl}
              onChange={(e) => setLocalTermsUrl(e.target.value)}
              onBlur={() => onTermsUrlChange?.(localTermsUrl)}
              placeholder="https://example.com/terms"
              className="h-7 text-xs bg-slate-800/40 border-slate-700/40 text-slate-200 placeholder:text-slate-600"
              data-testid="consent-terms-url"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleInsertTerms}
            disabled={!localTermsUrl}
            className="h-7 px-2 text-[10px] border-slate-700/40 text-slate-300 hover:text-indigo-300 hover:border-indigo-500/30 disabled:opacity-40"
            data-testid="consent-insert-terms-btn"
          >
            <Link2 className="h-3 w-3 mr-1" />
            Insert
          </Button>
        </div>
      </div>

      {/* Remove link utility */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleRemoveLink}
        className="h-6 px-2 text-[10px] text-slate-300 hover:text-red-400"
        data-testid="consent-remove-link-btn"
      >
        <Unlink className="h-3 w-3 mr-1" />
        Remove Link from Selection
      </Button>
    </div>
  );
}
